import {
  Device,
  PsmConfig
} from "@turnercode/types";
import { ResponseHandlers } from './enums'
import { ActiveConfigType, BundleConfigType } from '../utils/dataTypes'
import { PlayerState } from '../utils/dataTypes'

export type StringMap = Record<string, string>
export type ObjectMap = Record<string, any>
export type FunctionMap = Record<string, Function>

export type PsmVideoPlayer = any
export interface ITopVideoError {
  error: {
    _code: string
    _message: string
    _severity: string
  }
  type: string
  adBreakId?: string
}

export interface IMetaData<T> {
  brand: string
  subBrand: string
  platform: string
  environment: string
  schemaVersionNumber: string
  wmukid: string
  sessionId: string
  productName: string
  networkConnectionType: string
  appVersion: string
  deviceSpecificMetadata: T
  playerDependencies: any
  mappingMetadata: (unmapped: ObjectMap, keyMap: ObjectMap, playerDependencies: any) => ObjectMap
  setRootLevelProperties: (setPropertiesFunction) => void
  setLocationProperties: () => LocationMetadataType
  setLibraryProperties: () => LibraryMetadataType
}

export interface IPsmMgrConfig {
  psmTelSchemaVersion: string
  psmRolloutPercentages: {
    psmTel?: number
    psmOutbrain?: number
  }
}

export abstract class IStaticActiveConfig {
  static currentVersion: string
  static activeConfig: ActiveConfigType | BundleConfigType
  abstract updateCurrentVersion: (version: string) => void
}

export interface IActiveConfig {
  bundleConfig: BundleConfigType
  updateLocalStorageConfig: (newVersion: string) => Promise<void>
  getLocalStorageConfig: () => void
  setLocalStorageConfig: (data: ActiveConfigType | null) => void
}

export interface IManagerConfig {
  brand: string
  bootstrapVersion: string
  baseURL: string
  psmMgrConfig: IPsmMgrConfig | undefined
  currentPsmTelRollout: number | null
  getAndCheckPsmMgrConfigCDN: () => Promise<boolean>
  getThrottleFlag: () => boolean
}
export interface IPlayer {
  player: PsmVideoPlayer
  asyncMetadata: ObjectMap
  activateListeners: (eventBuilder: IEventBuilder, psmActiveConfigEvents?: string[]) => void
}

export interface IEventBuilder {
  videoSessionId: string
  videoSessionStartTimestamp: string
  eventCount: number
  eventSender: IEventSender
  heartbeatTimer: ITimer
  currentVersion: string
  playerState: Partial<PlayerState>
  isAdPaused: boolean
  bridge: IBridge
  getVideoStateAttribute: (type: string) => any
  setVideoStateAttributes: (type: string, data: any) => void
  queuePsmVidEvent: (eventName: string) => void
  clearVideoSession: () => void
  generateVideoSession: () => void
  getEventQueue: () => ObjectMap
  buildHeartbeatEvent: () => void
  setVideoError: (error: ITopVideoError) => void
}

export interface IBridge {
  psmProductConfig: IManagerConfig
  psmProgramMetadata: ObjectMap
  pollingTimer: ITimer | undefined
  psmActiveConfig: IActiveConfig
  playerDependencies: any
  metadataDependencies: { getMetadata: (playerDependencies: any) => IMetaData<DeviceMetadataWebType | DeviceMetadataReactNativeType> }
  eventBuilder: IEventBuilder
  setConfig: () => Promise<void>
  isBridgeInitialized: boolean
  setEventsToMonitor: (eventMap: StringMap) => void
  updateProgramMapStructure: (keyMap: ProgramMapType) => void
  setMetadataListener: (keyMap: ObjectMap) => void
  updateThrottling: () => void
  setPollingTimerForThrottling: () => void
}

export interface ITimer {
  interval: number
  callback: Function
  timer: number
  isTimerRunning: boolean
  setIntervalInMilliseconds: (intervalSeconds: number) => void
  start: () => number
  stop: () => number
  reset: () => number
  updateInterval: (newInterval: number) => number
}

export interface IResponseProcessor {
  handleResponse: (response: Response, eventSender: IEventSender) => void
}

export interface ResponseHandlerConfig {
  code: number
  handlerFunction: ResponseHandlers
}

export interface IEventSender {
  eventBuilder: IEventBuilder
  dispatchQueue: ObjectMap[]
  locationAccepted: boolean
  sendEvents: () => void
  getDispatchQueue: () => ObjectMap[]
  clearDispatchQueue: () => void
}
declare global {
  interface Window {
    psmVideo?: any
    psm3?: any
    TopPlayer: PsmVideoPlayer
    psmMgrConfig: PsmConfig
    psmMgr: {
      psm_test_ts: boolean
      psmEnvironment: string
      psmSendGeoInit: boolean
      psmHost: string
      psmGeoActualCookieValue: string
    }
    TOP: {
      Player: {
        Version: {
          libVersion: string
        }
      }
    }
    WM?: {
      UserConsent?: {
        inUserConsentState: (consentAgreements: string[]) => any
      }
      PSM?: any
    }
    cdpPsmCore?: { psm?: { browser?: string } }
    ZionMessageBus?: { getInstance: () => void }
    PubSub?: {
      subscribe: (topic: string, callback: () => void) => void
    }
    psmPubSubData?: any[]
    googletag: googletag.Googletag
  }
  interface Crypto {
    randomUUID: () => string;
  }
}

export type ProductPropertiesTypes = {
  productName: string
}

export type PlayerTypes = {
  playerVendor: string
  playerVersion: string
}

export type ToValue = {
  mapTo: string,
  type: string,
  enums?: string[]
}

export type CryptoVideoSessionId = {
  randomUUID: () => string
}

export type IPsmTelRnPlayerExtraDependencies = {
  localStorage: any;
  device: Device;
  appVersion: string;
  networkConnectionType: string;
};

export type LocationMetadataType = {
  city: string | null;
  state: string | null;
  country: string | null;
  timezone: string | null;
}

export type LibraryMetadataType = {
  psmSdkName: string | null;
  psmSdkVersion: string | null;
}

export type PlayerMetadataType = {
  playerVendor: string;
  playerVersion: string | null;
}

export type DeviceMetadataReactNativeType = {
  deviceModel: string;
  deviceBrand: string;
  deviceOs: string;
  deviceType: string;
  osVersion: string;
}

export type DeviceMetadataWebType = {
  cdpid: string;
  browserName: string;
  browserVersion: string;
  userAgent: string;
  referrer: string;
  rootDomain: string;
}

export type DeviceMapType = {
  documentation: string
  map: ToMapValue
}

export type ProgramMapType = {
  documentation: string
  players: ProgramPlayerType[]
}

type ToMapValue = {
  [mapFrom: string]: {
    mapTo: string,
    type: string,
    enums?: string[]
  },
}

type ProgramPlayerType = {
  playerName: string,
  map: ToMapValue
}
